import React from 'react';
import { twMerge } from 'tailwind-merge';
import type { ProductBadgeModel } from '@/models/product-badge-model';
import { ProductBadge, ProductBadgeSize } from './product-badge';

export interface ProductBadgeOverlayProps {
  badges: ProductBadgeModel[];
  /** Where the overlay sits over the image, e.g. "inset-x-3 top-3". */
  className?: string;
  badgeSize?: ProductBadgeSize;
}

function renderBadges(badges: ProductBadgeModel[], badgeSize?: ProductBadgeSize) {
  return badges.map(({
    key, label, color, icon,
  }) => <ProductBadge key={key} label={label} color={color} icon={icon} size={badgeSize} />);
}

/** Badges over a product image, in the top-left and top-right corners. */
export function ProductBadgeOverlay({
  badges,
  className,
  badgeSize,
}: Readonly<ProductBadgeOverlayProps>) {
  if (!badges.length) {
    return null;
  }

  const leftBadges = badges.filter((badge) => badge.position === 'top-left');
  const rightBadges = badges.filter((badge) => badge.position === 'top-right');

  return (
    <div
      className={twMerge('absolute z-20 flex justify-between gap-2', className)}
      data-testid="product-badge-overlay"
    >
      <div className="flex flex-wrap gap-2" data-testid="product-badge-corner-top-left">
        {renderBadges(leftBadges, badgeSize)}
      </div>
      <div
        className="flex flex-wrap justify-end gap-2"
        data-testid="product-badge-corner-top-right"
      >
        {renderBadges(rightBadges, badgeSize)}
      </div>
    </div>
  );
}
